import { SKELETON_ROWS_COUNT } from "@/config";
import { ListSkeletonProps } from "./types";
import { SkeletonCell } from "./skeleton-cells";

/**
 * Placeholder for the filter toolbar that RankListWithFilters renders above the
 * list (Filters button + quick filters / region select / period toggles). Used
 * in route loading.tsx + Suspense fallbacks so the list doesn't jump down by a
 * toolbar-row height when the real content streams in.
 */
export function FilterBarSkeleton() {
  return (
    <div className="mb-3 flex flex-wrap items-center gap-2">
      <div className="h-10 w-28 animate-pulse rounded-lg border border-[#2a475e]/50 bg-[#16202d]/50" />
      <div className="h-10 w-20 animate-pulse rounded-lg border border-[#2a475e]/50 bg-[#16202d]/50" />
      <div className="h-10 w-24 animate-pulse rounded-lg border border-[#2a475e]/50 bg-[#16202d]/50" />
    </div>
  );
}

export function ListSkeleton({
  columns,
  rowsCount = SKELETON_ROWS_COUNT,
  showMobileVersion = true,
}: ListSkeletonProps) {
  const skeletonRows = Array.from({ length: rowsCount }, (_, i) => i);
  const thClass = "py-1.5 px-2 text-left font-semibold text-[#c7d5e0] text-[11px] uppercase tracking-wide h-8";
  const tdClass = (noPadding?: boolean) => noPadding ? "p-0" : "py-1 px-2";

  return (
    <div className="bg-[#16202d] rounded-lg border border-[#2a475e] overflow-hidden">
      {/* Desktop Table Skeleton */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#1b2838] border-b border-[#2a475e]">
            <tr>
              {columns.map((column, index) => (
                <th key={index} className={thClass}>
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {skeletonRows.map((rowIndex) => (
              <tr
                key={rowIndex}
                className="border-b border-[#2a475e] last:border-b-0 hover:bg-[#1f2e3f] transition-colors cursor-pointer"
              >
                {columns.map((column, colIndex) => (
                  <td key={colIndex} className={tdClass(column.noPadding)}>
                    <SkeletonCell
                      type={column.type}
                      width={column.width}
                      customRender={column.customRender}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards Skeleton */}
      {showMobileVersion && (
        <div className="md:hidden">
          {skeletonRows.map((index) => (
            <div
              key={index}
              className="border-b border-[#2a475e] last:border-b-0 p-4 hover:bg-[#1f2e3f] transition-colors cursor-pointer"
            >
              <div className="flex gap-3">
                <div className="w-[120px] h-[45px] flex-shrink-0">
                  <div className="w-full h-full bg-[#1b2838] rounded border border-[#2a475e] animate-pulse" />
                </div>
                <div className="flex-1 min-w-0 space-y-2">
                  <div className="h-4 w-3/4 bg-[#1b2838] rounded animate-pulse" />
                  <div className="h-3 w-16 bg-[#1b2838] rounded animate-pulse" />
                  <div className="h-3 w-28 bg-[#1b2838] rounded animate-pulse" />
                  <div className="flex items-center gap-2">
                    <div className="h-3 w-12 bg-[#1b2838] rounded animate-pulse" />
                    <div className="flex gap-1 ml-auto">
                      <div className="w-4 h-4 bg-[#1b2838] rounded animate-pulse" />
                      <div className="w-4 h-4 bg-[#1b2838] rounded animate-pulse" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
